(() => {
  class RetailAnnouncement extends HTMLElement {
    connectedCallback() {
      const close = this.querySelector('[data-announcement-dismiss]');
      const key = `sk-announcement-${this.dataset.announcementId}`;
      try {
        if (sessionStorage.getItem(key) === 'dismissed') { this.hidden = true; return; }
      } catch (_) { /* Session storage may be unavailable. */ }
      close.hidden = false;
      close.addEventListener('click', () => {
        this.hidden = true;
        try { sessionStorage.setItem(key, 'dismissed'); } catch (_) { /* Dismissal still works for this page. */ }
      }, { once: true });
    }
  }

  class RetailHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const disclosures = () => this.querySelectorAll('[data-navigation], [data-header-menu]');
      this.addEventListener('toggle', event => {
        if (!event.target.matches('[data-navigation], [data-header-menu]') || !event.target.open) return;
        disclosures().forEach(menu => { if (menu !== event.target) menu.open = false; });
        this.querySelector('header-search')?.dismiss();
      }, { ...options, capture: true });
      this.addEventListener('header:panel-open', () => disclosures().forEach(menu => { menu.open = false; }), options);
      this.addEventListener('focusout', event => {
        if (!event.relatedTarget) return;
        disclosures().forEach(menu => { if (!menu.contains(event.relatedTarget)) menu.open = false; });
      }, options);
      this.breakpoint = matchMedia('(max-width: 1199px)');
      this.breakpoint.addEventListener('change', () => {
        disclosures().forEach(menu => { menu.open = false; });
        this.querySelector('header-search')?.dismiss();
        this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      }, options);
      this.observer?.disconnect();
      this.observer = new ResizeObserver(() => document.documentElement.style.setProperty('--retail-header-height', `${this.offsetHeight}px`));
      this.observer.observe(this);
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }

  class HeaderDialog extends HTMLElement {
    connectedCallback() {
      if (!window.HTMLDialogElement?.prototype.showModal) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.trigger = this.querySelector('summary');
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) {
        const panel = this.querySelector('[data-dialog-panel]');
        this.dialog = document.createElement('dialog');
        this.dialog.className = this.dataset.dialogClass;
        this.dialog.id = `${panel.id}-dialog`;
        this.dialog.setAttribute('aria-labelledby', panel.querySelector('h2').id);
        this.dialog.append(panel);
        this.append(this.dialog);
      }
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.setAttribute('aria-expanded', 'false');
      const alternateTriggers = [...this.querySelectorAll('[data-dialog-trigger]')].filter(trigger => trigger.closest('header-dialog, localization-dialog') === this);
      this.triggers = alternateTriggers.length ? alternateTriggers : [this.trigger];
      if (alternateTriggers.length) this.trigger.hidden = true;
      this.triggers.forEach(trigger => {
        trigger.hidden = false;
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      });
      const close = this.querySelector('[data-dialog-close]');
      close.hidden = false;
      this.triggers.forEach(trigger => trigger.addEventListener('click', event => {
        event.preventDefault();
        if (this.dialog.open) return;
        this.activeTrigger = trigger;
        this.closest('retail-header')?.querySelector('header-search')?.dismiss();
        this.dispatchEvent(new CustomEvent('header:panel-open', { bubbles: true }));
        this.dialog.showModal();
        this.dialog.querySelector('header-search')?.present();
        trigger.setAttribute('aria-expanded', 'true');
        this.dispatchEvent(new CustomEvent('header:dialog-open', { detail: { trigger } }));
      }, options));
      close.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.dialog.querySelector('header-search')?.reset();
        this.dialog.querySelector('.retail-preferences__form')?.reset();
        if (this.classList.contains('retail-drawer')) this.dialog.querySelectorAll('details[open]').forEach(details => { details.open = false; });
        this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
        if (this.activeTrigger?.getClientRects().length) this.activeTrigger.focus();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target.closest('a[href]')) this.dialog.close();
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
    }
    disconnectedCallback() { this.dialog?.close(); this.controller?.abort(); }
  }

  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-search-panel]');
      this.featured = this.querySelector('[data-search-featured]');
      this.footer = this.querySelector('[data-search-footer]');
      this.results = this.querySelector('[data-search-results]');
      this.message = this.querySelector('[data-search-message]');
      this.status = this.querySelector('[data-search-status]');
      this.all = this.querySelector('[data-search-all]');
      this.clear = this.querySelector('[data-search-clear]');
      this.endpoint = this.dataset.predictiveUrl;
      this.sequence = 0;
      this.active = -1;
      this.clear.hidden = !this.input.value;
      this.input.addEventListener('input', () => { this.clear.hidden = !this.input.value; }, options);
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.changed();
        this.input.focus();
      }, options);
      if (!this.endpoint) return;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.addEventListener('input', event => { if (!event.isComposing) this.changed(); }, options);
      this.input.addEventListener('compositionend', () => this.changed(), options);
      this.input.addEventListener('search', () => { if (!this.input.value) this.changed(); }, options);
      this.input.addEventListener('focus', () => { if (!this.restoringFocus && this.input.value.trim().length >= 2) this.changed(); }, options);
      this.input.addEventListener('keydown', event => this.keydown(event), options);
      this.form.addEventListener('submit', () => this.dismiss(), options);
      this.results.addEventListener('pointermove', event => {
        const option = event.target.closest('[role="option"]');
        if (option) this.select([...this.results.querySelectorAll('[role="option"]')].indexOf(option));
      }, options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.dismiss(); }, options);
      this.addEventListener('focusout', event => {
        const destination = event.relatedTarget;
        if (destination) {
          if (!this.contains(destination)) this.dismiss();
          return;
        }
        setTimeout(() => { if (!this.contains(document.activeElement)) this.dismiss(); });
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.dismiss();
          this.restoringFocus = true;
          this.input.focus();
          this.restoringFocus = false;
        }
      }, options);
    }
    changed() {
      this.dismiss();
      this.clear.hidden = !this.input.value;
      const term = this.input.value.trim();
      if (!this.endpoint || term.length < 2) { this.showFeatured(); return; }
      this.featured.hidden = true;
      this.footer.hidden = false;
      this.results.replaceChildren();
      this.message.textContent = this.dataset.loading;
      this.message.hidden = false;
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      this.open();
      this.timer = setTimeout(() => this.load(term), 250);
    }
    present() {
      if (this.input.value.trim().length >= 2) this.changed();
      else this.showFeatured();
    }
    reset() {
      this.dismiss();
      this.input.value = '';
      this.clear.hidden = true;
      this.showFeatured();
    }
    showFeatured() {
      this.panel.hidden = false;
      this.featured.hidden = false;
      this.footer.hidden = true;
      this.message.hidden = true;
      this.results.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
    }
    async load(term) {
      const sequence = ++this.sequence;
      this.request = new AbortController();
      const url = new URL(this.endpoint, location.origin);
      url.search = new URLSearchParams({ q: term, section_id: 'predictive-search', 'resources[type]': 'product,collection,article,page', 'resources[limit]': '8', 'resources[limit_scope]': 'all' });
      const allUrl = new URL(this.form.action);
      allUrl.search = new URLSearchParams({ q: term, type: 'product,article,page', 'options[prefix]': 'last' });
      this.all.href = allUrl.href;
      try {
        const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = parsed.querySelector('[data-predictive-response]');
        if (!fragment) throw new Error('Search response missing');
        if (sequence !== this.sequence || term !== this.input.value.trim()) return;
        // Keep IDs unique when a section is re-rendered or another search instance exists.
        fragment.querySelectorAll('[role="option"]').forEach(option => { option.id = `${this.results.id}-${option.id}`; });
        this.results.replaceChildren(...fragment.childNodes);
        this.message.hidden = true;
        this.status.textContent = fragment.dataset.announcement;
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.results.replaceChildren();
        this.message.textContent = this.dataset.error;
        this.message.hidden = false;
        this.status.textContent = this.dataset.error;
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }
    open() {
      this.dispatchEvent(new CustomEvent('header:panel-open', { bubbles: true }));
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    dismiss() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
      if (!this.input) return;
      this.panel.hidden = true;
      if (this.endpoint) this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.results.removeAttribute('aria-busy');
      this.active = -1;
      this.status.textContent = '';
    }
    select(index) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      this.active = index;
      if (options[index]) {
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      }
    }
    keydown(event) {
      if (event.isComposing) return;
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0 && options[this.active]) {
        event.preventDefault();
        options[this.active].click();
      } else if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        if (this.panel.hidden) { this.changed(); return; }
        if (options.length) this.select(this.active < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
      }
    }
    disconnectedCallback() { this.dismiss(); this.controller?.abort(); }
  }

  class LocalizationDialog extends HeaderDialog {
    connectedCallback() {
      super.connectedCallback();
      if (!this.controller) return;
      const options = { signal: this.controller.signal };
      const search = this.querySelector('[data-country-search]');
      const countries = [...this.querySelectorAll('[data-country-option]')];
      const languageCurrent = this.querySelector('[data-language-current]');
      const countryCurrent = this.querySelector('[data-country-current]');
      const tabs = [...this.querySelectorAll('[data-preferences-tab]')];
      const panels = [...this.querySelectorAll('[data-preferences-panel]')];
      const selectTab = (name, focus = false) => {
        tabs.forEach(tab => {
          const active = tab.dataset.preferencesTab === name;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        panels.forEach(panel => { panel.hidden = panel.dataset.preferencesPanel !== name; });
      };
      if (tabs.length) {
        this.querySelector('[data-preferences-tabs]').hidden = false;
        selectTab('country');
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => selectTab(tab.dataset.preferencesTab), options);
          tab.addEventListener('keydown', event => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
            selectTab(tabs[next].dataset.preferencesTab, true);
          }, options);
        });
      }
      const updateLanguage = () => {
        const selected = this.querySelector('[name="language_code"]:checked + .retail-preferences__language-row [lang]');
        if (selected && languageCurrent) languageCurrent.textContent = selected.textContent;
      };
      const updateCountry = () => {
        const selected = this.querySelector('[name="country_code"]:checked + .retail-preferences__country-row');
        if (selected && countryCurrent) countryCurrent.textContent = `${selected.querySelector('.retail-preferences__country-name').textContent} · ${selected.querySelector('.retail-preferences__country-currency').textContent.split('·')[0].trim()}`;
      };
      const normalize = value => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const filterCountries = () => {
        const query = normalize(search.value.trim());
        countries.forEach(country => { country.hidden = !normalize(country.dataset.search).includes(query); });
      };
      search?.addEventListener('input', filterCountries, options);
      this.querySelector('.retail-preferences__form')?.addEventListener('change', () => { updateLanguage(); updateCountry(); }, options);
      this.dialog?.addEventListener('close', () => {
        if (search) filterCountries();
        if (tabs.length) selectTab('country');
        updateLanguage();
        updateCountry();
      }, options);
      this.addEventListener('header:dialog-open', event => {
        const target = event.detail.trigger.dataset.preferencesTarget || (search ? 'country' : 'language');
        if (tabs.length) selectTab(target);
        if (target === 'country' && search) {
          search.focus();
          this.querySelector('[name="country_code"]:checked')?.closest('[data-country-option]')?.scrollIntoView({ block: 'center' });
        } else {
          this.querySelector('[name="language_code"]:checked')?.focus();
        }
      }, options);
    }
  }

  for (const [name, component] of [['retail-announcement', RetailAnnouncement], ['retail-header', RetailHeader], ['header-dialog', HeaderDialog], ['localization-dialog', LocalizationDialog], ['header-search', HeaderSearch]]) {
    if (!customElements.get(name)) customElements.define(name, component);
  }
})();
